import Guid from '../../shared/guid';
import { Layer } from './Layer';
import { ShapeBase } from './Shapes/Shape';
import { Media } from './Media';
import { Settings } from './Settings';
import { FormattedExpression } from './FormattedExpression';
import { LabelTag } from './LabelTag';

export class LabelHeader {
  public id: string;
  public organisationID: string;
  public name: string;
  public description: string;
  public authorId: string;
  public created: Date;
  public lastUpdated: Date;
  public queryID: string;
  public addOns: Array<string>;
  public cultureCode: string;
  public mediaTemplateID: string;
  public locked: boolean;
  /** Tags on this label - only populated by the label list, never stored in the label document. */
  public tags: LabelTag[];

  constructor(labelHeader?: LabelHeader) {
    this.update(labelHeader);
  }

  update(labelHeader: LabelHeader) {
    if (labelHeader) {
      this.id = labelHeader.id;
      this.organisationID = labelHeader.organisationID;
      this.name = labelHeader.name;
      this.description = labelHeader.description;
      this.authorId = labelHeader.authorId;
      this.created = labelHeader.created;
      this.lastUpdated = labelHeader.lastUpdated;
      this.queryID = labelHeader.queryID;
      this.addOns = labelHeader.addOns;
      this.cultureCode = labelHeader.cultureCode;
      this.mediaTemplateID = labelHeader.mediaTemplateID;
      this.locked = labelHeader.locked;
      this.tags = labelHeader.tags?.map(t => new LabelTag(t));
    }
  }

}

export class Label extends LabelHeader {
  get topLayer(): Layer {
    let top: Layer;
    if (!this.layers || this.layers.length === 0) {
      this.layers = new Array<Layer>();
      top = new Layer();
      this.layers.push(top);
    } else {
      top = this.layers[this.layers.length - 1];
    }
    return top;
  }

  get topHeaderLayer(): Layer {
    let top: Layer;
    if (!this.headerLayers || this.headerLayers.length === 0) {
      this.headerLayers = new Array<Layer>();
      top = new Layer();
      this.headerLayers.push(top);
    } else {
      top = this.headerLayers[this.headerLayers.length - 1];
    }
    return top;
  }

  get topFooterLayer(): Layer {
    let top: Layer;
    if (!this.footerLayers || this.footerLayers.length === 0) {
      this.footerLayers = new Array<Layer>();
      top = new Layer();
      this.footerLayers.push(top);
    } else {
      top = this.footerLayers[this.footerLayers.length - 1];
    }
    return top;
  }

  getTopLayerForArea(area: string): Layer {
    switch (area) {
      case 'header':
        return this.topHeaderLayer;
      case 'footer':
        return this.topFooterLayer;
      case 'body':
      default:
        return this.topLayer;
    }
  }

  public settings: Settings;
  public media: Media;
  public layers: Layer[];
  public headerLayers: Layer[];
  public footerLayers: Layer[];
  public variables: Array<FormattedExpression>;

  constructor(label?: Label) {
    super(label);
    this.update(label);
  }

  update(label: Label) {
    this.settings = new Settings();
    this.media = new Media();
    this.layers = new Array<Layer>();
    this.headerLayers = new Array<Layer>();
    this.footerLayers = new Array<Layer>();
    this.variables = new Array<FormattedExpression>();
    if (label) {
      super.update(label);
      this.settings = new Settings(label.settings);
      this.media = new Media(label.media);

      if (label.layers) {
        label.layers.forEach(layer => {
          this.layers.push(new Layer(layer));
        });
      }

      if (label.headerLayers) {
        label.headerLayers.forEach(layer => {
          this.headerLayers.push(new Layer(layer));
        });
      }

      if (label.footerLayers) {
        label.footerLayers.forEach(layer => {
          this.footerLayers.push(new Layer(layer));
        });
      }

      if (label.variables) {
        label.variables.forEach(v => {
          this.variables.push(new FormattedExpression(v));
        });
      }

      this.ensureUniqueShapeIds();
    }
  }

  /**
   * Reissue ids only where a label actually contains a duplicate (#1555).
   *
   * Shapes keep the id they were saved with, so the server and the browser can talk about the
   * same shape. Some stored labels do contain duplicates though — a copy/paste bug reused the
   * source's id for a long time — and the designer's selection-dedup treats two shapes sharing
   * an id as one, quietly dropping the second. Minting a fresh id for the later copy is the
   * smallest change that avoids that, and it leaves every well-formed label untouched.
   *
   * The label analyser reports and repairs these server-side as well, so the stored copy gets
   * fixed properly; this just makes sure an unrepaired one still behaves in the designer.
   */
  private ensureUniqueShapeIds(): void {
    const seen = new Set<string>();

    const visit = (shapes: ShapeBase[]) => {
      shapes?.forEach(shape => {
        if (!shape) {
          return;
        }

        if (!shape.id || seen.has(shape.id)) {
          shape.id = Guid.create().toString();
        }
        seen.add(shape.id);

        // Group members are shapes in their own right and are just as capable of colliding.
        visit((shape as unknown as { shapes?: ShapeBase[] }).shapes);
      });
    };

    [...this.layers, ...this.headerLayers, ...this.footerLayers]
      .forEach(layer => visit(layer?.shapes));
  }

}

export class LabelPreview extends LabelHeader {
  svg: string;
  queryName: string;
  mediaName: string;
}
